import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL, apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import MasterPartForm from "@/components/forms/MasterPartForm";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown
} from "lucide-react";


const AddServiceForm = ({ onServiceAdded, formData, setFormData }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Vehicle compatibility state
  const [vehicleCompatibility, setVehicleCompatibility] = useState({
    compatible_makes: [],
    compatible_models: [],
    compatible_series: [],
    compatible_years: []
  });
  
  // Selected values from dropdowns
  const [selectedVehicle, setSelectedVehicle] = useState({
    make: '',
    model: '',
    series: '',
    year: ''
  });
  
  // Vehicle data for dropdowns
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  
  // Use React Hook Form for proper state management
  const { register, handleSubmit, reset, control, formState: { errors }, setValue } = useForm({
    defaultValues: {
      name: formData.name || '',
      description: formData.description || '',
      category: formData.category || 'maintenance',
      duration: formData.duration || '',
      price: formData.price || '',
      skill_level: formData.skill_level || 'intermediate',
      sac_code: formData.sac_code || '',
      gst_rate: formData.gst_rate != null ? String(formData.gst_rate) : '18'
    }
  });

  // Sync external formData changes with RHF (if needed)
  useEffect(() => {
    reset({
      name: formData.name || '',
      description: formData.description || '',
      category: formData.category || 'maintenance',
      duration: formData.duration || '',
      price: formData.price || '',
      skill_level: formData.skill_level || 'intermediate',
      sac_code: formData.sac_code || '',
      gst_rate: formData.gst_rate != null ? String(formData.gst_rate) : '18'
    });
  }, [formData, reset]);
  
  // Fetch vehicle makes for dropdowns
  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setAvailableMakes(makes);
      } catch (error) {
        console.error('Error fetching vehicle makes:', error);
        setAvailableMakes([]);
      }
    };
    
    fetchVehicleMakes();
  }, []);

  // Fetch vehicle models when make changes
  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedVehicle.make) {
        setAvailableModels([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedVehicle.make)}`);
        const models = (response.data.models || []).filter(m => m && m.trim() !== '');
        setAvailableModels(models);
      } catch (error) {
        console.error('Error fetching vehicle models:', error);
        setAvailableModels([]);
      }
    };
    fetchModels();
  }, [selectedVehicle.make]);

  // Fetch vehicle series when make+model changes
  useEffect(() => {
    const fetchSeries = async () => {
      if (!selectedVehicle.make || !selectedVehicle.model) {
        setAvailableSeries([]);
        return;
      }
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedVehicle.make)}/${encodeURIComponent(selectedVehicle.model)}`);
        const series = (response.data.series || []).filter(s => s && s.trim() !== '');
        setAvailableSeries(series);
      } catch (error) {
        console.error('Error fetching vehicle series:', error);
        setAvailableSeries([]);
      }
    };
    fetchSeries();
  }, [selectedVehicle.make, selectedVehicle.model]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      const serviceData = {
        name: data.name?.trim() || '',
        category: data.category || 'maintenance',
        description: data.description?.trim() || '',
        duration: parseFloat(data.duration) || 0,
        price: parseFloat(data.price) || 0,
        skill_level: data.skill_level || 'intermediate',
        sac_code: (data.sac_code || '').trim() || null,
        gst_rate: parseFloat(data.gst_rate) || 0,
        // Add vehicle compatibility data
        compatible_makes: vehicleCompatibility.compatible_makes,
        compatible_models: vehicleCompatibility.compatible_models,
        compatible_series: vehicleCompatibility.compatible_series,
        compatible_years: vehicleCompatibility.compatible_years
      };

      if (!serviceData.name || serviceData.name.length === 0) {
        toast.error("Please enter service name");
        setIsSubmitting(false);
        return;
      }

      // Save to backend database
      console.log('Submitting service data:', serviceData); // Debug log
      const response = await axios.post(`${API}/services`, serviceData);
      
      // Sync back to parent state
      setFormData(serviceData);
      
      // Reset vehicle compatibility
      setVehicleCompatibility({
        compatible_makes: [],
        compatible_models: [],
        compatible_series: [],
        compatible_years: []
      });
      
      toast.success('Service added successfully!');
      reset(); // Clear form after successful submission
      onServiceAdded(response.data); // Pass the service data with ID from backend
    } catch (error) {
      console.error('Error adding service:', error);
      toast.error('Failed to add service');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle vehicle compatibility selection
  const handleVehicleSelect = () => {
    const { make, model, series, year } = selectedVehicle;
    
    console.log('Selected vehicle:', selectedVehicle); // Debug log
    
    if (!make || !model) {
      toast.error("Please select at least Make and Model");
      return;
    }
    
    setVehicleCompatibility(prev => {
      const newState = { ...prev };
      
      // Add make if not exists
      if (make && !newState.compatible_makes.includes(make)) {
        newState.compatible_makes = [...newState.compatible_makes, make];
      }
      
      // Add model if not exists
      if (model && !newState.compatible_models.includes(model)) {
        newState.compatible_models = [...newState.compatible_models, model];
      }
      
      // Add series if not exists
      if (series && !newState.compatible_series.includes(series)) {
        newState.compatible_series = [...newState.compatible_series, series];
      }
      
      // Add year if not exists
      if (year && !newState.compatible_years.includes(parseInt(year))) {
        newState.compatible_years = [...newState.compatible_years, parseInt(year)].sort((a, b) => b - a);
      }
      
      console.log('Updated vehicle compatibility:', newState); // Debug log
      return newState;
    });
    
    // Reset selected vehicle after adding
    setSelectedVehicle({ make: '', model: '', series: '', year: '' });
  };

  // Remove compatibility entries
  const removeCompatibilityItem = (type, item) => {
    setVehicleCompatibility(prev => ({
      ...prev,
      [`compatible_${type}`]: prev[`compatible_${type}`].filter(i => i !== item)
    }));
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Add New Service</DialogTitle>
        <DialogDescription>
          Create a new service offering for your garage
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Service Name *</Label>
            <Input
              id="name"
              {...register('name', { required: 'Service name is required' })}
              placeholder="e.g., Oil Change Service"
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="add-service-category">Category</Label>
            <Controller
              name="category"
              control={control}
              defaultValue="maintenance"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="add-service-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="diagnostic">Diagnostic</SelectItem>
                    <SelectItem value="inspection">Inspection</SelectItem>
                    <SelectItem value="bodywork">Body Work</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            {...register('description')}
            placeholder="Detailed description of the service"
            rows="3"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="duration">Duration (hours)</Label>
            <Input
              id="duration"
              type="number"
              {...register('duration')}
              placeholder="2.5"
              min="0"
              step="0.5"
            />
          </div>
          <div>
            <Label htmlFor="price">Price (₹) *</Label>
            <Input
              id="price"
              type="number"
              {...register('price', { required: 'Price is required' })}
              placeholder="1500"
              min="0"
              step="50"
            />
            {errors.price && (
              <p className="text-red-500 text-sm mt-1">{errors.price.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="add-service-skill-level">Skill Level</Label>
            <Controller
              name="skill_level"
              control={control}
              defaultValue="intermediate"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="add-service-skill-level">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="beginner">Beginner</SelectItem>
                    <SelectItem value="intermediate">Intermediate</SelectItem>
                    <SelectItem value="advanced">Advanced</SelectItem>
                    <SelectItem value="expert">Expert</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sac_code">SAC Code (GST)</Label>
            <Input
              id="sac_code"
              {...register('sac_code')}
              placeholder="e.g. 998714"
              data-testid="add-service-sac-input"
            />
          </div>
          <div>
            <Label htmlFor="add-service-gst-rate">GST Rate (%)</Label>
            <Controller
              name="gst_rate"
              control={control}
              defaultValue="18"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="add-service-gst-rate" data-testid="add-service-gst-rate"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0% (Exempt)</SelectItem>
                    <SelectItem value="5">5%</SelectItem>
                    <SelectItem value="12">12%</SelectItem>
                    <SelectItem value="18">18%</SelectItem>
                    <SelectItem value="28">28%</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        {/* Vehicle Compatibility Section */}
        <div className="border-t pt-6">
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium">Vehicle Compatibility</h3>
              <p className="text-sm text-muted-foreground">
                Select compatible vehicles for this service (optional)
              </p>
            </div>

            {/* Vehicle Selection Dropdowns */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {/* MAKE Dropdown */}
                <div>
                  <Label htmlFor="vehicle_make">MAKE</Label>
                  <Select
                    value={selectedVehicle.make}
                    onValueChange={(value) => {
                      setSelectedVehicle(prev => ({
                        ...prev,
                        make: value,
                        model: '', // Reset model when make changes
                        series: '', // Reset series when make changes
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Make" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMakes.map((make) => (
                        <SelectItem key={make} value={make}>
                          {make}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* MODEL Dropdown */}
                <div>
                  <Label htmlFor="vehicle_model">MODEL</Label>
                  <Select
                    value={selectedVehicle.model}
                    onValueChange={(value) => {
                      setSelectedVehicle(prev => ({
                        ...prev,
                        model: value,
                        series: '', // Reset series when model changes
                      }));
                    }}
                    disabled={!selectedVehicle.make}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Model" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableModels.map((model) => (
                          <SelectItem key={model} value={model}>
                            {model}
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>

                {/* SERIES Dropdown */}
                <div>
                  <Label htmlFor="vehicle_series">SERIES</Label>
                  <Select
                    value={selectedVehicle.series}
                    onValueChange={(value) => {
                      setSelectedVehicle(prev => ({
                        ...prev,
                        series: value
                      }));
                    }}
                    disabled={!selectedVehicle.make || !selectedVehicle.model}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Series" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableSeries.map((series) => (
                          <SelectItem key={series} value={series}>
                            {series}
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>

                {/* YEAR Input */}
                <div>
                  <Label htmlFor="vehicle_year">YEAR</Label>
                  <Input
                    id="vehicle_year"
                    type="number"
                    min="1980"
                    max="2030"
                    value={selectedVehicle.year}
                    onChange={(e) => {
                      setSelectedVehicle(prev => ({
                        ...prev,
                        year: e.target.value
                      }));
                    }}
                    placeholder="e.g., 2020"
                  />
                </div>
              </div>
              
              {/* Add Vehicle Button */}
              <Button 
                type="button"
                variant="outline"
                onClick={handleVehicleSelect}
                className="w-auto"
                disabled={!selectedVehicle.make || !selectedVehicle.model}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Vehicle Compatibility
              </Button>
            </div>

            {/* Display Selected Compatibility */}
            {(vehicleCompatibility.compatible_makes.length > 0 || 
              vehicleCompatibility.compatible_models.length > 0 ||
              vehicleCompatibility.compatible_series.length > 0 ||
              vehicleCompatibility.compatible_years.length > 0) && (
              <div className="space-y-3">
                <h4 className="font-medium text-sm">Compatible Vehicles:</h4>
                
                {vehicleCompatibility.compatible_makes.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Makes:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {vehicleCompatibility.compatible_makes.map((make, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {make}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => removeCompatibilityItem('makes', make)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {vehicleCompatibility.compatible_models.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Models:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {vehicleCompatibility.compatible_models.map((model, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {model}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => removeCompatibilityItem('models', model)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {vehicleCompatibility.compatible_series.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Series:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {vehicleCompatibility.compatible_series.map((series, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {series}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => removeCompatibilityItem('series', series)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {vehicleCompatibility.compatible_years.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Years:</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {vehicleCompatibility.compatible_years.map((year, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {year}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-4 w-4 p-0 ml-1 hover:bg-destructive hover:text-destructive-foreground"
                            onClick={() => removeCompatibilityItem('years', year)}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-2 pt-4">
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? "Adding Service..." : "Add Service"}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Edit Service Form Component

const AddPartToPurchaseOrderForm = ({ onSuccess, onCancel }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Cascading dropdown state for vehicle information
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');

  // Dynamic vehicle data from backend API  
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  const [vehicleDataLoading, setVehicleDataLoading] = useState(false);

  // Suppliers data from API
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  
  // Use React Hook Form for proper state management
  const { 
    register, 
    handleSubmit, 
    control, 
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm({
    defaultValues: {
      // Vehicle Information
      make: '',
      model: '',
      series: '',
      year: '',
      // Part Information
      part_name: '',
      part_number: '',
      brand: '',
      description: '',
      category: 'others',
      supplier: '',
      supplier_contact: '',
      cost_price: '',
      selling_price: '',
      stock_quantity: '',
      minimum_stock: '1',
      location: 'Warehouse'
    }
  });

  // Fetch vehicle makes from API
  const fetchVehicleMakes = useCallback(async () => {
    try {
      setVehicleDataLoading(true);
      const response = await axios.get(`${backendUrl}/api/vehicle-data/makes`);
      const makes = (response.data.makes || []).filter(make => make && make.trim() !== '');
      setAvailableMakes(makes);
    } catch (error) {
      console.error('Error fetching vehicle makes:', error);
      // Fallback to basic makes if API fails
      setAvailableMakes(['Mercedes-Benz', 'BMW', 'Audi', 'Toyota', 'Honda', 'Other']);
    } finally {
      setVehicleDataLoading(false);
    }
  }, [backendUrl]);

  // Fetch vehicle models for selected make
  const fetchVehicleModels = useCallback(async (make) => {
    if (!make) {
      setAvailableModels([]);
      return;
    }

    try {
      const response = await axios.get(`${backendUrl}/api/vehicle-data/models/${encodeURIComponent(make)}`);
      const models = (response.data.models || []).filter(model => model && model.trim() !== '');
      setAvailableModels(models);
    } catch (error) {
      console.error('Error fetching vehicle models:', error);
      setAvailableModels([]);
    }
  }, [backendUrl]);

  // Fetch vehicle series for selected make and model
  const fetchVehicleSeries = useCallback(async (make, model) => {
    if (!make || !model) {
      setAvailableSeries([]);
      return;
    }

    try {
      const response = await axios.get(`${backendUrl}/api/vehicle-data/series/${encodeURIComponent(make)}/${encodeURIComponent(model)}`);
      const series = (response.data.series || []).filter(s => s && s.trim() !== '');
      setAvailableSeries(series);
    } catch (error) {
      console.error('Error fetching vehicle series:', error);
      setAvailableSeries([]);
    }
  }, [backendUrl]);

  // Fetch suppliers from API
  const fetchSuppliers = useCallback(async () => {
    try {
      setSuppliersLoading(true);
      const response = await axios.get(`${backendUrl}/api/suppliers`);
      const suppliers = response.data || [];
      setAvailableSuppliers(suppliers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setSuppliersLoading(false);
    }
  }, [backendUrl]);

  // Initialize vehicle makes and suppliers on component mount
  useEffect(() => {
    fetchVehicleMakes();
    fetchSuppliers();
  }, [fetchVehicleMakes, fetchSuppliers]);

  // Listen for vehicle data updates from "Add New Vehicle Data" feature
  useEffect(() => {
    const handleVehicleDataUpdate = () => {
      fetchVehicleMakes();
      if (selectedMake) {
        fetchVehicleModels(selectedMake);
      }
      if (selectedMake && selectedModel) {
        fetchVehicleSeries(selectedMake, selectedModel);
      }
    };

    window.addEventListener('vehicleDataUpdated', handleVehicleDataUpdate);
    return () => window.removeEventListener('vehicleDataUpdated', handleVehicleDataUpdate);
  }, [selectedMake, selectedModel, fetchVehicleMakes, fetchVehicleModels, fetchVehicleSeries]);

  // Fetch models when make changes
  useEffect(() => {
    fetchVehicleModels(selectedMake);
  }, [selectedMake, fetchVehicleModels]);

  // Fetch series when make or model changes
  useEffect(() => {
    fetchVehicleSeries(selectedMake, selectedModel);
  }, [selectedMake, selectedModel, fetchVehicleSeries]);

  const categories = [
    'engine',
    'transmission',
    'brakes',
    'suspension',
    'electrical',
    'exhaust',
    'cooling',
    'fuel_system',
    'ac_system',
    'body',
    'interior',
    'filters',
    'oils_fluids',
    'tires',
    'others'
  ];

  // Watch cost_price for real-time validation and selling price calculation
  const watchCostPrice = watch('cost_price');
  const watchSellingPrice = watch('selling_price');
  const watchCategory = watch('category');

  // Liquid categories use Liters (L) and accept decimal quantities
  const isLiquidCategory = watchCategory === 'oils_fluids';
  const quantityUnit = isLiquidCategory ? 'L' : 'pcs';
  const quantityStep = isLiquidCategory ? '0.1' : '1';

  // Auto-calculate selling price based on cost price (30% markup)
  useEffect(() => {
    const costPrice = parseFloat(watchCostPrice) || 0;
    if (costPrice > 0 && !watchSellingPrice) {
      const suggestedSellingPrice = (costPrice * 1.3).toFixed(2);
      setValue('selling_price', suggestedSellingPrice);
    }
  }, [watchCostPrice, watchSellingPrice, setValue]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      // Validate required fields
      if (!data.part_name.trim()) {
        toast.error("Part name is required");
        setIsSubmitting(false);
        return;
      }
      
      if (!data.part_number.trim()) {
        toast.error("Part number is required");
        setIsSubmitting(false);
        return;
      }

      const partData = {
        part_name: data.part_name.trim(),
        part_number: data.part_number.trim().toUpperCase(),
        description: data.description?.trim() || '',
        category: data.category,
        cost_price: parseFloat(data.cost_price) || 0,
        selling_price: parseFloat(data.selling_price) || parseFloat(data.cost_price) * 1.3 || 0,
        stock_quantity: parseFloat(data.stock_quantity) || 0,
        minimum_stock: parseFloat(data.minimum_stock) || 5,
        supplier: data.supplier?.trim() || selectedSupplier?.trim() || '',
        supplier_contact: data.supplier_contact?.trim() || '',
        location: data.location?.trim() || 'Warehouse',
        brand: data.brand?.trim() || '',
        // Vehicle compatibility using cascading dropdown values
        compatible_makes: selectedMake ? [selectedMake.trim()] : [],
        compatible_models: selectedModel ? [selectedModel.trim()] : [],
        compatible_series: selectedSeries ? [selectedSeries.trim()] : [],
        compatible_years: data.year ? [parseInt(data.year)] : [],
        // Additional vehicle info in notes
        notes: `Vehicle Info: ${selectedMake} ${selectedModel} ${selectedSeries} ${data.year}`.trim().replace(/\s+/g, ' ')
      };

      const response = await apiFetch(`${API}/parts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        },
        body: JSON.stringify(partData)
      });

      if (response.ok) {
        const newPart = await response.json();
        toast.success('Part created and added to purchase order!');
        reset(); // Reset form after successful submission
        onSuccess(newPart);
      } else {
        const error = await response.text();
        toast.error('Failed to create part: ' + error);
      }
    } catch (error) {
      console.error('Error creating part:', error);
      toast.error('Failed to create part. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormError = (errors) => {
    console.log('Form validation errors:', errors);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      toast.error(firstError.message || 'Please check the form fields');
    }
  };

  return (
    <div className="space-y-6">
      <DialogHeader>
        <DialogTitle>Add New Part</DialogTitle>
        <DialogDescription>
          Create a new part and add it to the purchase order
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit(onSubmit, handleFormError)} className="space-y-6">
        {/* Vehicle Information Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">🚗 Vehicle Compatibility (Optional)</h4>
          <p className="text-xs text-gray-600 mb-4">Select compatible vehicle MAKE → MODEL → SERIES for this part</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* MAKE Dropdown */}
            <div>
              <Label htmlFor="make">MAKE</Label>
              <Select
                value={selectedMake}
                onValueChange={(value) => {
                  setSelectedMake(value);
                  setValue('make', value);
                  // Reset model and series when make changes
                  setSelectedModel('');
                  setSelectedSeries('');
                  setValue('model', '');
                  setValue('series', '');
                }}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select vehicle MAKE" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {vehicleDataLoading ? (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      Loading makes...
                    </div>
                  ) : availableMakes.length > 0 ? (
                    availableMakes
                      .filter(make => make && make.trim() !== '')
                      .map((make) => (
                        <SelectItem key={make} value={make}>
                          {make}
                        </SelectItem>
                      ))
                  ) : (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No vehicle makes available
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* MODEL Dropdown */}
            <div>
              <Label htmlFor="model">MODEL</Label>
              <Select
                value={selectedModel}
                onValueChange={(value) => {
                  setSelectedModel(value);
                  setValue('model', value);
                  // Reset series when model changes
                  setSelectedSeries('');
                  setValue('series', '');
                }}
                disabled={!selectedMake}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue 
                    placeholder={
                      selectedMake 
                        ? `Select ${selectedMake} MODEL` 
                        : "Select MAKE first"
                    } 
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableModels.length > 0 ? (
                    availableModels.map((model) => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))
                  ) : selectedMake ? (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No predefined models for {selectedMake}
                    </div>
                  ) : null}
                  {selectedMake && selectedMake !== 'Other' && (
                    <SelectItem value="Other Model">Other {selectedMake} Model</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {selectedMake && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableModels.length > 0 
                    ? `${availableModels.length} ${selectedMake} MODELs available`
                    : `Select ${selectedMake} MODEL`
                  }
                </div>
              )}
            </div>

            {/* SERIES Dropdown */}
            <div>
              <Label htmlFor="series">SERIES</Label>
              <Select
                value={selectedSeries}
                onValueChange={(value) => {
                  setSelectedSeries(value);
                  setValue('series', value);
                }}
                disabled={!selectedMake || !selectedModel}
              >
                <SelectTrigger className="bg-white">
                  <SelectValue 
                    placeholder={
                      !selectedMake ? "Select MAKE first" :
                      !selectedModel ? "Select MODEL first" :
                      `Select ${selectedModel} SERIES`
                    } 
                  />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableSeries.length > 0 ? (
                    availableSeries.map((series) => (
                      <SelectItem key={series} value={series}>
                        {series}
                      </SelectItem>
                    ))
                  ) : selectedMake && selectedModel ? (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No predefined series for {selectedModel}
                    </div>
                  ) : null}
                  {selectedMake && selectedModel && selectedModel !== 'Other Model' && (
                    <SelectItem value="Other Series">Other {selectedModel} Series</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {selectedMake && selectedModel && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableSeries.length > 0 
                    ? `${availableSeries.length} ${selectedModel} SERIES available`
                    : `Select ${selectedModel} SERIES`
                  }
                </div>
              )}
              {!selectedMake || !selectedModel ? (
                <p className="text-xs text-gray-500 mt-1">Select MAKE and MODEL first to see SERIES options</p>
              ) : null}
            </div>
            
            {/* Year Field (unchanged) */}
            <div>
              <Label htmlFor="year">Year</Label>
              <Input
                id="year"
                type="number"
                min="1990"
                max="2030"
                placeholder="e.g., 2020"
                {...register('year', {
                  min: { value: 1990, message: 'Year must be 1990 or later' },
                  max: { value: 2030, message: 'Year must be 2030 or earlier' }
                })}
                className="mt-1"
              />
              {errors.year && (
                <p className="text-red-500 text-xs mt-1">{errors.year.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* Part Information Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">Part Information *</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="part_name">Part Name *</Label>
              <Input
                id="part_name"
                placeholder="e.g., Engine Oil Filter"
                {...register('part_name', {
                  required: 'Part name is required',
                  minLength: { value: 2, message: 'Part name must be at least 2 characters' }
                })}
                className={`mt-1 ${errors.part_name ? 'border-red-500' : ''}`}
              />
              {errors.part_name && (
                <p className="text-red-500 text-xs mt-1">{errors.part_name.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="part_number">Part Number *</Label>
              <Input
                id="part_number"
                placeholder="e.g., OF001, 15208-43G00"
                {...register('part_number', {
                  required: 'Part number is required',
                  minLength: { value: 2, message: 'Part number must be at least 2 characters' }
                })}
                className={`mt-1 uppercase ${errors.part_number ? 'border-red-500' : ''}`}
                style={{ textTransform: 'uppercase' }}
              />
              {errors.part_number && (
                <p className="text-red-500 text-xs mt-1">{errors.part_number.message}</p>
              )}
            </div>
            
            <div>
              <Label htmlFor="brand">Brand</Label>
              <Input
                id="brand"
                placeholder="e.g., Bosch, Mann Filter"
                {...register('brand')}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label htmlFor="category">Category *</Label>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Category is required' }}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={`mt-1 ${errors.category ? 'border-red-500' : ''}`}>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat.split('_').map(word => 
                            word.charAt(0).toUpperCase() + word.slice(1)
                          ).join(' ')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.category && (
                <p className="text-red-500 text-xs mt-1">{errors.category.message}</p>
              )}
            </div>
            
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Detailed description of the part..."
                {...register('description')}
                className="mt-1"
                rows={3}
              />
            </div>
            
            <div>
              <Label htmlFor="cost_price">Cost Price (₹) *</Label>
              <Input
                id="cost_price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                {...register('cost_price', {
                  required: 'Cost price is required',
                  min: { value: 0, message: 'Cost price must be non-negative' },
                  validate: value => {
                    const num = parseFloat(value);
                    return !isNaN(num) && num >= 0 || 'Please enter a valid price';
                  }
                })}
                className={`mt-1 ${errors.cost_price ? 'border-red-500' : ''}`}
              />
              {errors.cost_price && (
                <p className="text-red-500 text-xs mt-1">{errors.cost_price.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="selling_price">Selling Price (₹) *</Label>
              <Input
                id="selling_price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                {...register('selling_price', {
                  required: 'Selling price is required',
                  min: { value: 0, message: 'Selling price must be non-negative' },
                  validate: value => {
                    const num = parseFloat(value);
                    const costPrice = parseFloat(watchCostPrice) || 0;
                    if (isNaN(num) || num < 0) return 'Please enter a valid selling price';
                    if (costPrice > 0 && num < costPrice) {
                      return 'Selling price should not be less than cost price';
                    }
                    return true;
                  }
                })}
                className={`mt-1 ${errors.selling_price ? 'border-red-500' : ''}`}
              />
              {errors.selling_price && (
                <p className="text-red-500 text-xs mt-1">{errors.selling_price.message}</p>
              )}
              {watchCostPrice && watchSellingPrice && parseFloat(watchSellingPrice) > 0 && parseFloat(watchCostPrice) > 0 && (
                <p className="text-green-600 text-xs mt-1">
                  Profit margin: {(((parseFloat(watchSellingPrice) - parseFloat(watchCostPrice)) / parseFloat(watchCostPrice)) * 100).toFixed(1)}%
                </p>
              )}
              {watchCostPrice && parseFloat(watchCostPrice) > 0 && !watchSellingPrice && (
                <p className="text-blue-600 text-xs mt-1">
                  Suggested: ₹{(parseFloat(watchCostPrice) * 1.3).toFixed(2)} (30% markup)
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Supplier Information Section */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">Supplier Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="supplier">Supplier *</Label>
              <Select
                value={selectedSupplier}
                onValueChange={(value) => {
                  setSelectedSupplier(value);
                  setValue('supplier', value);
                  
                  // Auto-fill supplier contact if available
                  const supplier = availableSuppliers.find(s => s.name === value);
                  if (supplier && supplier.phone) {
                    setValue('supplier_contact', supplier.phone);
                  }
                }}
              >
                <SelectTrigger className="mt-1 bg-white">
                  <SelectValue placeholder="Select supplier" />
                </SelectTrigger>
                <SelectContent className="max-h-60">
                  {suppliersLoading ? (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      Loading suppliers...
                    </div>
                  ) : availableSuppliers.length > 0 ? (
                    availableSuppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.name}>
                        {supplier.name}
                        {supplier.contact_person && (
                          <span className="text-gray-500 ml-2">({supplier.contact_person})</span>
                        )}
                      </SelectItem>
                    ))
                  ) : (
                    <div className="p-2 text-sm text-gray-500 text-center">
                      No suppliers available
                    </div>
                  )}
                  <SelectItem value="Other">Add New Supplier</SelectItem>
                </SelectContent>
              </Select>
              {selectedSupplier && availableSuppliers.length > 0 && (
                <div className="text-xs text-gray-500 mt-1">
                  {availableSuppliers.length} suppliers available
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="supplier_contact">Supplier Contact</Label>
              <Input
                id="supplier_contact"
                placeholder="E.G., +91-9876543210"
                {...register('supplier_contact')}
                className="mt-1"
              />
              {selectedSupplier && selectedSupplier !== 'Other' && (
                <div className="text-xs text-gray-500 mt-1">
                  Auto-filled from supplier database
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stock Information */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">📦 Stock Information</h4>
          {isLiquidCategory && (
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
              💧 Liquid category detected — quantity is measured in <strong>Liters (L)</strong> and supports decimals (e.g., 1.5 L)
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="stock_quantity">
                Initial Quantity in Stock <span className="text-blue-600 font-semibold">({quantityUnit})</span>
              </Label>
              <Input
                id="stock_quantity"
                type="number"
                min="0"
                step={quantityStep}
                placeholder={isLiquidCategory ? '0.0' : '0'}
                data-testid="po-part-stock-quantity-input"
                {...register('stock_quantity', {
                  min: { value: 0, message: 'Quantity cannot be negative' }
                })}
                className={`mt-1 ${errors.stock_quantity ? 'border-red-500' : ''}`}
              />
              {errors.stock_quantity && (
                <p className="text-red-500 text-xs mt-1">{errors.stock_quantity.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                {isLiquidCategory ? 'Volume in Liters (decimals allowed, e.g., 1.5)' : 'Number of units currently in stock'}
              </p>
            </div>

            <div>
              <Label htmlFor="minimum_stock">
                Minimum Stock Level <span className="text-blue-600 font-semibold">({quantityUnit})</span>
              </Label>
              <Input
                id="minimum_stock"
                type="number"
                min="0"
                step={quantityStep}
                placeholder={isLiquidCategory ? '1.0' : '1'}
                data-testid="po-part-minimum-stock-input"
                {...register('minimum_stock', {
                  min: { value: 0, message: 'Minimum stock cannot be negative' }
                })}
                className={`mt-1 ${errors.minimum_stock ? 'border-red-500' : ''}`}
              />
              {errors.minimum_stock && (
                <p className="text-red-500 text-xs mt-1">{errors.minimum_stock.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Alert when stock falls below this level</p>
            </div>

            <div>
              <Label htmlFor="location">Storage Location</Label>
              <Input
                id="location"
                placeholder="E.G., WAREHOUSE, SHELF A-3"
                data-testid="po-part-location-input"
                {...register('location')}
                className="mt-1"
                style={{ textTransform: 'uppercase' }}
              />
              <p className="text-xs text-gray-500 mt-1">Where this part is stored</p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Creating Part...
              </>
            ) : (
              'Create Part & Add to Order'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

// Edit Purchase Order Form Component

const AddExistingItemsToInvoiceForm = ({ itemType, items = [], onItemsSelected, onCancel, targetLabel = 'invoice' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);
  // Selected items map: { item_id: { item_id, name, description, quantity, unit_price } }
  const [selectedItemsMap, setSelectedItemsMap] = useState({});

  const isService = itemType === 'service';
  const itemTypeLabel = isService ? 'Service' : 'Part';
  const [showCreateForm, setShowCreateForm] = useState(false);

  const partCategories = [
    'engine', 'transmission', 'brakes', 'suspension', 'electrical', 'body',
    'interior', 'exhaust', 'cooling', 'fuel_system', 'ac_system', 'filters',
    'oils_fluids', 'tires', 'others'
  ];
  const categoryLabel = (cat) => cat.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  const handleInlinePartCreated = (savedPart) => {
    const brands = Array.isArray(savedPart.brands) ? savedPart.brands : [];
    const b = brands.length > 0 ? brands[0] : null;
    const price = b
      ? (parseFloat(b.selling_price) || parseFloat(b.cost_price) || 0)
      : (parseFloat(savedPart.selling_price) || parseFloat(savedPart.cost_price) || 0);
    onItemsSelected([{
      item_id: savedPart.id,
      name: savedPart.part_name,
      description: savedPart.description || savedPart.part_name,
      quantity: 1,
      unit_price: price,
      brand_id: b ? b.id : null,
      brand_name: b ? b.brand_name : null,
    }]);
  };

  // Fetch makes
  useEffect(() => {
    const fetchMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setAvailableMakes(makes.sort());
      } catch (error) {
        setAvailableMakes([]);
      }
    };
    fetchMakes();
  }, []);

  // Fetch models on make change
  useEffect(() => {
    if (!selectedMake) {
      setAvailableModels([]);
      return;
    }
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        const models = (response.data.models || []).filter(m => m && m.trim() !== '');
        setAvailableModels(models.sort());
      } catch (error) {
        setAvailableModels([]);
      }
    };
    fetchModels();
  }, [selectedMake]);

  // Fetch series on model change
  useEffect(() => {
    if (!selectedMake || !selectedModel) {
      setAvailableSeries([]);
      return;
    }
    const fetchSeries = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        const series = (response.data.series || []).filter(s => s && s.trim() !== '');
        setAvailableSeries(series.sort());
      } catch (error) {
        setAvailableSeries([]);
      }
    };
    fetchSeries();
  }, [selectedMake, selectedModel]);

  const clearFilters = () => {
    setSelectedMake('');
    setSelectedModel('');
    setSelectedSeries('');
    setSearchTerm('');
    setCategoryFilter('');
  };

  // Filter items: apply vehicle compatibility filter & search
  const filteredItems = items.filter(item => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const name = (item.name || item.part_name || '').toLowerCase();
      const partNum = (item.part_number || '').toLowerCase();
      const desc = (item.description || '').toLowerCase();
      if (!name.includes(term) && !partNum.includes(term) && !desc.includes(term)) {
        return false;
      }
    }
    // Category filter (parts only)
    if (!isService && categoryFilter && item.category !== categoryFilter) {
      return false;
    }
    // Vehicle compatibility filter (strict): when a make/model/series is chosen,
    // only parts explicitly tagged as compatible are shown. Parts with no
    // compatibility data are hidden while a filter is active.
    const ciIncludes = (arr, val) =>
      Array.isArray(arr) && arr.some(x => String(x).trim().toLowerCase() === String(val).trim().toLowerCase());
    if (selectedMake) {
      if (!ciIncludes(item.compatible_makes, selectedMake)) return false;
    }
    if (selectedModel) {
      if (!ciIncludes(item.compatible_models, selectedModel)) return false;
    }
    if (selectedSeries) {
      if (!ciIncludes(item.compatible_series, selectedSeries)) return false;
    }
    return true;
  });

  const toggleItem = (item) => {
    setSelectedItemsMap(prev => {
      const newMap = { ...prev };
      if (newMap[item.id]) {
        delete newMap[item.id];
      } else {
        const brands = (!isService && Array.isArray(item.brands)) ? item.brands : [];
        const b = brands.length > 0 ? brands[0] : null;
        const price = isService
          ? (parseFloat(item.price) || 0)
          : (b
              ? (parseFloat(b.selling_price) || parseFloat(b.cost_price) || 0)
              : (parseFloat(item.selling_price) || parseFloat(item.cost_price) || 0));
        newMap[item.id] = {
          item_id: item.id,
          name: item.name || item.part_name,
          description: item.description || item.name || item.part_name,
          quantity: 1,
          unit_price: price,
          brand_id: b ? b.id : null,
          brand_name: b ? b.brand_name : null,
        };
      }
      return newMap;
    });
  };

  const updateItemBrand = (item, brandId) => {
    const b = (item.brands || []).find(x => x.id === brandId);
    setSelectedItemsMap(prev => ({
      ...prev,
      [item.id]: {
        ...prev[item.id],
        brand_id: b ? b.id : prev[item.id]?.brand_id,
        brand_name: b ? b.brand_name : prev[item.id]?.brand_name,
        unit_price: b ? (parseFloat(b.selling_price) || parseFloat(b.cost_price) || 0) : prev[item.id]?.unit_price,
      }
    }));
  };

  const updateQuantity = (itemId, quantity) => {
    setSelectedItemsMap(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity: Math.max(0.01, parseFloat(quantity) || 1) }
    }));
  };

  const updatePrice = (itemId, price) => {
    setSelectedItemsMap(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], unit_price: Math.max(0, parseFloat(price) || 0) }
    }));
  };

  const handleSubmit = () => {
    const selected = Object.values(selectedItemsMap);
    if (selected.length === 0) {
      toast.error(`Please select at least one ${itemType}`);
      return;
    }
    onItemsSelected(selected);
  };

  const selectedCount = Object.keys(selectedItemsMap).length;

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>Add Existing {itemTypeLabel}s</DialogTitle>
        <DialogDescription>
          Find and add {itemTypeLabel.toLowerCase()}s to the {targetLabel}. Use filters to narrow by vehicle compatibility.
        </DialogDescription>
      </DialogHeader>

      {/* Vehicle Compatibility Filters */}
      <div className="p-3 bg-gray-50 rounded-lg">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-sm font-medium text-gray-900">Vehicle Compatibility Filters (Optional)</h4>
          <Button type="button" variant="outline" size="sm" onClick={clearFilters}>
            Clear All
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Make</Label>
            <select
              className="w-full h-9 px-2 rounded-md border border-gray-300 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={selectedMake}
              onChange={(e) => { setSelectedMake(e.target.value); setSelectedModel(''); setSelectedSeries(''); }}
              data-testid="invoice-existing-filter-make"
            >
              <option value="">All Makes</option>
              {availableMakes.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Model</Label>
            <select
              className="w-full h-9 px-2 rounded-md border border-gray-300 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={selectedModel}
              onChange={(e) => { setSelectedModel(e.target.value); setSelectedSeries(''); }}
              disabled={!selectedMake}
              data-testid="invoice-existing-filter-model"
            >
              <option value="">{selectedMake ? 'All Models' : 'Select Make first'}</option>
              {availableModels.map(m => (<option key={m} value={m}>{m}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Series</Label>
            <select
              className="w-full h-9 px-2 rounded-md border border-gray-300 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={selectedSeries}
              onChange={(e) => setSelectedSeries(e.target.value)}
              disabled={!selectedModel}
              data-testid="invoice-existing-filter-series"
            >
              <option value="">{selectedModel ? 'All Series' : 'Select Model first'}</option>
              {availableSeries.map(s => (<option key={s} value={s}>{s}</option>))}
            </select>
          </div>
          {!isService && (
            <div>
              <Label className="text-xs">Category</Label>
              <select
                className="w-full h-9 px-2 rounded-md border border-gray-300 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                data-testid="invoice-existing-filter-category"
              >
                <option value="">All Categories</option>
                {partCategories.map(c => (<option key={c} value={c}>{categoryLabel(c)}</option>))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Search */}
      <div>
        <Label className="text-xs">Search {itemTypeLabel}s</Label>
        <input
          type="text"
          placeholder={`Search by name${isService ? '' : ' or part number'}...`}
          className="w-full h-10 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          defaultValue={searchTerm}
          onInput={(e) => setSearchTerm(e.target.value)}
          data-testid={`invoice-existing-${itemType}-search`}
        />
        <p className="text-xs text-gray-500 mt-1">
          Showing {filteredItems.length} of {items.length} {itemTypeLabel.toLowerCase()}(s)
        </p>
      </div>

      {/* Items list */}
      <div className="border rounded-md max-h-80 overflow-y-auto">
        {filteredItems.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 space-y-3">
            <p>No {itemTypeLabel.toLowerCase()}s match the current filters.</p>
            {!isService && !showCreateForm && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowCreateForm(true)}
                data-testid="empty-create-part-btn"
              >
                + Create New Part
              </Button>
            )}
          </div>
        ) : (
          filteredItems.map(item => {
            const isSelected = !!selectedItemsMap[item.id];
            const selected = selectedItemsMap[item.id];
            const displayName = item.name || item.part_name;
            const price = isService
              ? (parseFloat(item.price) || 0)
              : (parseFloat(item.selling_price) || parseFloat(item.cost_price) || 0);
            return (
              <div
                key={item.id}
                className={`p-3 border-b last:border-b-0 transition-colors ${
                  isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleItem(item)}
                    className="mt-1 h-4 w-4 cursor-pointer"
                    data-testid={`invoice-existing-${itemType}-checkbox-${item.id}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900">
                          {displayName} {(!isService && Array.isArray(item.brands) && item.brands.length > 1)
                            ? null
                            : (item.part_number ? <span className="text-gray-500 text-xs">({item.part_number})</span> : null)}
                        </div>
                        {item.description && (
                          <div className="text-xs text-gray-600 mt-0.5 line-clamp-2">{item.description}</div>
                        )}
                        {!isService && (
                          <div className="mt-1 flex items-center gap-2 flex-wrap">
                            {(Array.isArray(item.brands) && item.brands.length > 1) ? (
                              <span className="text-xs px-2 py-0.5 rounded-full border bg-blue-100 text-blue-800 border-blue-300">
                                {item.brands.length} brands · pick one below
                              </span>
                            ) : (() => {
                              const stock = parseFloat(item.stock_quantity || 0);
                              const minStock = parseFloat(item.minimum_stock || 0);
                              const unit = item.category === 'oils_fluids' ? 'L' : 'pcs';
                              let badgeClass = 'bg-green-100 text-green-800 border-green-300';
                              let label = 'In Stock';
                              if (stock === 0) {
                                badgeClass = 'bg-red-100 text-red-800 border-red-300';
                                label = 'Out of Stock';
                              } else if (stock < minStock) {
                                badgeClass = 'bg-amber-100 text-amber-800 border-amber-300';
                                label = 'Low Stock';
                              }
                              return (
                                <span className={`text-xs px-2 py-0.5 rounded-full border ${badgeClass}`} data-testid={`existing-part-stock-${item.id}`}>
                                  {label}: {stock} {unit}
                                </span>
                              );
                            })()}
                            {item.location && (
                              <span className="text-xs text-gray-500">📍 {item.location}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="text-sm font-medium text-green-700 whitespace-nowrap">
                        ₹{price.toLocaleString()}
                      </div>
                    </div>

                    {isSelected && (
                      <div className="mt-2 space-y-2">
                        {!isService && Array.isArray(item.brands) && item.brands.length > 0 && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <Label className="text-xs">Brand (internal — hidden on print)</Label>
                            <Select
                              value={selected.brand_id || ''}
                              onValueChange={(v) => updateItemBrand(item, v)}
                            >
                              <SelectTrigger className="h-8 text-sm" data-testid={`invoice-existing-part-brand-${item.id}`}>
                                <SelectValue placeholder="Select brand" />
                              </SelectTrigger>
                              <SelectContent>
                                {item.brands.map((b) => (
                                  <SelectItem key={b.id} value={b.id}>
                                    {b.brand_name}{b.part_number ? ` — ${b.part_number}` : ''} · Stock {b.stock_quantity ?? 0} · ₹{(b.selling_price ?? 0)}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Quantity</Label>
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={selected.quantity}
                              onChange={(e) => updateQuantity(item.id, e.target.value)}
                              className="h-8 text-sm"
                              data-testid={`invoice-existing-${itemType}-qty-${item.id}`}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Unit Price (₹)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={selected.unit_price}
                              onChange={(e) => updatePrice(item.id, e.target.value)}
                              className="h-8 text-sm"
                              data-testid={`invoice-existing-${itemType}-price-${item.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {!isService && showCreateForm && (
        <div className="border rounded-md p-4 bg-gray-50 space-y-3" data-testid="inline-create-part-wrapper">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">Create New Part</span>
            <Button type="button" variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
              Cancel
            </Button>
          </div>
          <MasterPartForm
            onSuccess={handleInlinePartCreated}
            onCancel={() => setShowCreateForm(false)}
          />
        </div>
      )}

      <div className="flex justify-between items-center pt-2 border-t">
        <span className="text-sm text-gray-600">
          {selectedCount} {itemTypeLabel.toLowerCase()}(s) selected
        </span>
        <div className="flex gap-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={selectedCount === 0}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid={`invoice-add-selected-${itemType}-btn`}
          >
            Add {selectedCount > 0 ? `${selectedCount} ` : ''}{itemTypeLabel}{selectedCount !== 1 ? 's' : ''} to {targetLabel.charAt(0).toUpperCase() + targetLabel.slice(1)}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Add Invoice Form Component

export { AddServiceForm, AddPartToPurchaseOrderForm, AddExistingItemsToInvoiceForm };
